<template>
  <footer v-if="isAuthenticated" class="app-footer">
    <div class="app-footer__row">
      <button class="app-footer__link" type="button" @click="openSupport">
        {{ $t('footer.support') }}
      </button>
      <a
        class="app-footer__link"
        href="/law/offer"
        target="_blank"
        rel="noopener"
      >
        {{ $t('footer.offer') }}
      </a>
      <button class="app-footer__link app-footer__link--danger" type="button" @click="handleLogout">
        {{ $t('auth.logout') }}
      </button>
    </div>
    <div class="app-footer__copyright">{{ $t('footer.copyright', { year: currentYear }) }}</div>
  </footer>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'

import { useProfileStore } from '@/modules/profile/store/profileStore'
import { hasConsent, readConsent, writeConsent } from '@/shared/consent'

// Window.Intercom is already declared (as `any`) by IntercomWidget.vue,
// which mounts in AppLayout and loads the widget once authenticated.

const profileStore = useProfileStore()
const { profile } = storeToRefs(profileStore)
const isAuthenticated = computed(() => !!profile.value)
const currentYear = new Date().getFullYear()

const openSupport = () => {
  // IntercomWidget.vue (mounted in AppLayout) loads the widget once the
  // profile is available — this just opens it, mirrors the existing
  // Techподдержка launcher.
  if (typeof window.Intercom === 'function') {
    window.Intercom('show')
    return
  }

  // Виджета нет — значит категория «Поддержка и диагностика» ещё не
  // разрешена: до согласия скрипт Intercom не грузится вовсе
  // (shared/consent.ts, IntercomWidget::initIntercomWhenAllowed).
  //
  // Клик по «Поддержка» — это и есть явная просьба открыть чат, поэтому он
  // считается согласием на категорию: записываем выбор (остальное не трогаем)
  // и открываем чат сразу, без окна настроек. Раньше клик открывал
  // «Настройки согласия», и человек вместо чата видел переключатели cookie.
  // IntercomWidget подписан на событие согласия и по нему ставит стаб
  // window.Intercom, копящий вызовы до загрузки скрипта: 'show' не потеряется.
  if (!hasConsent('support')) {
    writeConsent({ analytics: readConsent()?.analytics === true, support: true })
  }
  window.Intercom?.('show')
}

const handleLogout = () => {
  profileStore.logout()
}
</script>

<style scoped lang="scss">
// IGRA-20 shell footer: «Поддержка · Оферта · Выйти» — short, mobile-first
// (base width 560px, key.igra.click reference). No language/country/social
// clutter — that belongs to the marketing site, not the VPN cabinet.
.app-footer {
  margin-top: auto;
  padding: 20px 16px calc(20px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid var(--ds-border);
  background: var(--ds-surface-canvas);

  &__row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
    max-width: 560px;
    margin: 0 auto;
  }

  &__link {
    background: none;
    border: none;
    padding: 4px;
    color: var(--ds-text-secondary);
    font-size: var(--ds-text-size-body);
    font-weight: var(--ds-weight-medium);
    text-decoration: none;
    cursor: pointer;

    &:hover {
      color: var(--ds-text);
    }
  }

  &__link--danger:hover {
    color: var(--ds-danger);
  }

  &__copyright {
    margin-top: 12px;
    text-align: center;
    color: var(--ds-text-muted);
    font-size: var(--ds-text-size-caption);
  }
}
</style>
